import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../utils/Utils.js';
import { formatDateTime } from '../../utils/dateFormat.js';
import DataCollection from '../shared/DataCollection.js';
import TextInput from '../shared/inputs/TextInput.js';
import Patch from '../utils/Patch.js';
import '../herd/ListHerd.css';
import '../shared/DefaultBadge.css';
import './ListsList.css';
import { formatListCode } from './listCode';

// ProjectLink shows a list's finance project, read-only; clicking it opens
// the project (Trello 70qmkpWY). Editing the link stays on the list's page.
const ProjectLink = ({ project }) => {
  const navigate = useNavigate();
  if (!project) return null;
  return (
    <span className="lists-list-link lists-project-link"
      onClick={e => { e.stopPropagation(); navigate(`/finances/projects/${project._id}`); }}>
      {project.code} — {project.name}
    </span>
  );
};

const ListsListCard = ({ row, onListClick, onSetDefault, itemCount, project }) => {
  const { t } = useTranslation();
  return (
    <div className="lists-list-card" onClick={() => onListClick(row)}>
      <div className="lists-list-card-name">{formatListCode(row, t)}</div>
      {row.name && <div className="lists-list-card-subname">{row.name}</div>}
      {project && <div className="lists-list-card-project">{t('lists_project')}: <ProjectLink project={project} /></div>}
      <div className="lists-list-card-meta">
        {row.created_by && <span>{row.created_by} · </span>}
        <span>{itemCount} {t('lists_item_count')}</span>
        {row.expires_at && (
          <span> · {t('lists_expires_at')}: {formatDateTime(row.expires_at, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
        )}
      </div>
      <div className="lists-list-card-footer">
        {row.status === 'archived'
          ? <span className="lists-archived-badge">{t('lists_filter_archived')}</span>
          : row.is_default
            ? <span className="default-badge">★ {t('default')}</span>
            : <button className="set-default-btn" onClick={e => { e.stopPropagation(); onSetDefault(row); }}>{t('set_as_default')}</button>
        }
      </div>
    </div>
  );
};

const ListsListCards = ({ data, onListClick, onSetDefault, itemCounts, projectsById }) => (
  <div className="lists-list-cards">
    {(data || []).map(row => (
      <ListsListCard key={row._id} row={row} onListClick={onListClick} onSetDefault={onSetDefault}
        itemCount={itemCounts[row._id] || 0} project={projectsById[row.project_id]} />
    ))}
    {(!data || data.length === 0) && (
      <div className="lists-list-empty">—</div>
    )}
  </div>
);

export const ListsList = ({ onListClick, refreshKey, onDefaultSet, actions }) => {
  const { t } = useTranslation();
  const [internalKey,  setInternalKey]  = useState(0);
  const [listStatus,   setListStatus]   = useState('active');
  const [filtersOpen,  setFiltersOpen]  = useState(false);
  const [isMobile,     setIsMobile]     = useState(window.innerWidth < 768);
  const [itemCounts,   setItemCounts]   = useState({});
  const [countsKey,    setCountsKey]    = useState(0);
  const [projectsById, setProjectsById] = useState({});

  useEffect(() => {
    Fetch('api/finance/projects', 'GET', null,
      (data) => setProjectsById(Object.fromEntries((data || []).map(p => [p._id, p]))),
      () => {});
  }, []);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Fetch(
      '/api/data/lists.items',
      'GET', null,
      (data) => {
        const counts = {};
        (data || []).forEach(item => {
          if (item.list_id) counts[item.list_id] = (counts[item.list_id] || 0) + 1;
        });
        setItemCounts(counts);
        setCountsKey(k => k + 1);
      },
      () => {}
    );
  }, [refreshKey]);

  const handleSetDefault = (row) => {
    Fetch(
      '/api/data/lists.lists',
      'GET', null,
      (data) => {
        const oldDefault = (data || []).find(l => l.is_default && l._id !== row._id && l.status !== 'archived');
        const doSet = () => Fetch(
          '/api/data/lists.lists', 'PATCH',
          JSON.stringify({ _id: row._id, is_default: true }),
          () => { setInternalKey(k => k + 1); onDefaultSet?.(row); },
          () => {}
        );
        if (oldDefault) {
          Fetch(
            '/api/data/lists.lists', 'PATCH',
            JSON.stringify({ _id: oldDefault._id, is_default: false }),
            doSet, doSet
          );
        } else {
          doSet();
        }
      },
      () => {}
    );
  };

  const activeFilterCount = listStatus === 'archived' ? 1 : 0;

  const resetFilters = () => setListStatus('active');

  const searchFields = listStatus === 'both'
    ? {}
    : { status: listStatus };

  const columns = [
    {
      name: t('lists_list'),
      selector: row => formatListCode(row, t),
      sortable: true,
      width: '130px',
      cell: row => (
        <span className="lists-list-link" onClick={() => onListClick(row)}>
          {formatListCode(row, t)}
        </span>
      ),
    },
    {
      name: t('name'),
      selector: row => row.name || '',
      sortable: true,
      grow: 2,
      cell: row => (
        <TextInput
          value={row.name || ''}
          onBlur={(e) => Patch('lists.lists', row._id, 'name', e.target.value,
            () => setInternalKey(k => k + 1),
            () => {}
          )}
        />
      ),
    },
    {
      name: t('lists_project'),
      selector: row => projectsById[row.project_id]?.code || '',
      sortable: true,
      grow: 2,
      cell: row => projectsById[row.project_id] ? <ProjectLink project={projectsById[row.project_id]} /> : '—',
    },
    {
      name: t('lists_item_count'),
      selector: row => itemCounts[row._id] || 0,
      sortable: true,
      width: '90px',
      cell: row => itemCounts[row._id] || 0,
    },
    {
      name: t('lists_expires_at'),
      selector: row => row.expires_at || '',
      sortable: true,
      width: '140px',
      cell: row => row.expires_at ? formatDateTime(row.expires_at, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—',
    },
    {
      name: t('lists_added_by'),
      selector: row => row.created_by || '—',
      sortable: true,
      width: '120px',
    },
    {
      name: t('default'),
      width: '150px',
      cell: row => row.status === 'archived'
        ? <span className="lists-archived-badge">{t('lists_filter_archived')}</span>
        : row.is_default
          ? <span className="default-badge">★ {t('default')}</span>
          : <button className="set-default-btn" onClick={() => handleSetDefault(row)}>{t('set_as_default')}</button>,
    },
  ];

  const FilterControls = () => (
    <select value={listStatus} onChange={e => { setListStatus(e.target.value); setFiltersOpen(false); }}>
      <option value="active">{t('lists_filter_active')}</option>
      <option value="archived">{t('lists_filter_archived')}</option>
      <option value="both">{t('lists_filter_both')}</option>
    </select>
  );

  return (
    <div>
      {isMobile ? (
        <div className="mobile-search-filter">
          <button
            className={`mobile-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            {t('filters')}
            {activeFilterCount > 0 && <span className="mobile-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      ) : (
        <div className="desktop-filter-bar">
          <div className="desktop-filter-top">
            <button
              className={`desktop-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
              onClick={() => setFiltersOpen(o => !o)}
            >
              {t('filters')}
              {activeFilterCount > 0 && <span className="desktop-filter-badge">{activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && (
              <button className="desktop-filter-reset" onClick={resetFilters}>
                {t('reset_filters')}
              </button>
            )}
            {actions && <div className="desktop-filter-actions">{actions}</div>}
          </div>
          {filtersOpen && (
            <div className="desktop-filter-panel">
              <FilterControls />
            </div>
          )}
        </div>
      )}

      <DataCollection
        key={`${refreshKey}-${internalKey}-${listStatus}-${countsKey}`}
        collection="lists.lists"
        searchFields={searchFields}
        columns={columns}
        sortField={1}
        mobileComponent={(data) => (
          <ListsListCards data={data} onListClick={onListClick} onSetDefault={handleSetDefault}
            itemCounts={itemCounts} projectsById={projectsById} />
        )}
      />

      {isMobile && filtersOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t('filters')}</span>
              <button className="filter-sheet-close" onClick={() => setFiltersOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              <FilterControls />
            </div>
            <div className="filter-sheet-footer">
              <button
                className="filter-sheet-reset"
                onClick={() => { resetFilters(); setFiltersOpen(false); }}
                disabled={activeFilterCount === 0}
              >
                {t('reset_filters')}
              </button>
              <button className="filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
                {t('done')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListsList;
